/**
 * ModelSelector - Dropdown for selecting Gemini models.
 *
 * Displays model name, description, and token limits.
 */

import { ChevronDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import type { GeminiModel } from '@/api/settings'

interface ModelSelectorProps {
  label: string
  description: string
  value: string
  models: GeminiModel[]
  onChange: (model: string) => void
  disabled?: boolean
}

export function ModelSelector({
  label,
  description,
  value,
  models,
  onChange,
  disabled = false,
}: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Find current model info
  const currentModel = models.find((m) => m.name === value)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="space-y-2">
      <div>
        <div className="text-text font-medium">{label}</div>
        <div className="text-sm text-text-muted">{description}</div>
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full flex items-center justify-between gap-2 px-3 py-2
            bg-surface-alt border border-surface-border rounded-lg
            text-left transition-colors
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-hover cursor-pointer'}
          `}
        >
          <div className="flex-1 min-w-0">
            <div className="text-text font-mono text-sm truncate">
              {currentModel?.display_name || value}
            </div>
            {currentModel && (
              <div className="text-xs text-text-muted truncate">
                {currentModel.description}
              </div>
            )}
          </div>
          <ChevronDown
            className={`w-4 h-4 text-text-muted flex-shrink-0 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 py-1 bg-surface border border-surface-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {models.map((model) => (
              <button
                key={model.name}
                type="button"
                onClick={() => {
                  onChange(model.name)
                  setIsOpen(false)
                }}
                className={`
                  w-full px-3 py-2 text-left transition-colors
                  ${model.name === value ? 'bg-accent/10 text-accent' : 'hover:bg-surface-hover'}
                `}
              >
                <div className="font-mono text-sm">{model.display_name}</div>
                <div className="text-xs text-text-muted mt-0.5">{model.description}</div>
                {model.input_token_limit && (
                  <div className="text-[10px] text-text-muted mt-1">
                    {(model.input_token_limit / 1000).toFixed(0)}K input / {' '}
                    {model.output_token_limit ? `${(model.output_token_limit / 1000).toFixed(0)}K output` : 'N/A'}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ModelSelector
